import type { Breadcrumb, Event } from "@sentry/react";

/**
 * The request payload attached to a Sentry event. Derived from `Event` because
 * `@sentry/react` does not export the request type by name.
 */
type SentryRequest = NonNullable<Event["request"]>;

/**
 * Marker written in place of any value that has been removed for privacy.
 */
const REDACTED = "[Filtered]";

/**
 * Header names (compared case-insensitively) whose values carry auth
 * credentials or PII and must never reach Sentry.
 */
const SENSITIVE_HEADER_KEYS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-id-token",
  "x-access-token",
  "x-refresh-token",
]);

/**
 * Strip the query string from a URL, replacing it with a redaction marker.
 *
 * URLs frequently carry tokens (e.g. the WebSocket `?id_token=<JWT>`), so the
 * entire query is removed rather than redacting individual keys. The path and
 * hash fragment are preserved so the event stays useful for debugging.
 *
 * Works for both absolute and relative URLs.
 */
export const redactUrl = (url: string): string => {
  if (!url) {
    return url;
  }

  const hashIndex = url.indexOf("#");
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;

  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex < 0) {
    return url;
  }

  const base = withoutHash.slice(0, queryIndex);
  return `${base}?${REDACTED}${hash}`;
};

/**
 * Redact the values of sensitive headers (auth tokens, cookies) while leaving
 * non-sensitive header names and values intact.
 */
const redactHeaders = (
  headers: Record<string, string>,
): Record<string, string> => {
  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(headers)) {
    sanitized[key] = SENSITIVE_HEADER_KEYS.has(key.toLowerCase())
      ? REDACTED
      : value;
  }

  return sanitized;
};

/**
 * Remove tokens and PII from a Sentry request payload: the URL query string,
 * the standalone `query_string` field, auth headers, and cookies.
 */
const sanitizeRequest = (request: SentryRequest): SentryRequest => {
  const sanitized: SentryRequest = { ...request };

  if (typeof sanitized.url === "string") {
    sanitized.url = redactUrl(sanitized.url);
  }

  if (sanitized.query_string !== undefined) {
    sanitized.query_string = REDACTED;
  }

  // Cookies are structured data, not a plain string — drop them entirely
  // rather than redacting individual values.
  if (sanitized.cookies !== undefined) {
    delete sanitized.cookies;
  }

  if (sanitized.headers) {
    sanitized.headers = redactHeaders(sanitized.headers);
  }

  return sanitized;
};

/**
 * Breadcrumb `data` keys that hold URLs/paths and may carry token-bearing
 * query strings: `url` (fetch/xhr) and `from`/`to` (navigation route changes).
 */
const BREADCRUMB_URL_KEYS = ["url", "from", "to"] as const;

/**
 * Scrub a single breadcrumb, redacting the query string of any URL/path
 * recorded in its `data` (fetch/xhr `url`, navigation `from`/`to`).
 */
export const scrubSentryBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (!breadcrumb.data) {
    return breadcrumb;
  }

  const data = { ...breadcrumb.data };
  let didRedact = false;

  for (const key of BREADCRUMB_URL_KEYS) {
    if (typeof data[key] === "string") {
      data[key] = redactUrl(data[key]);
      didRedact = true;
    }
  }

  return didRedact ? { ...breadcrumb, data } : breadcrumb;
};

/**
 * Scrub a Sentry error event before it is sent: redact tokens/PII from the
 * request payload and from every attached breadcrumb URL.
 */
export const scrubSentryEvent = <T extends Event>(event: T): T => {
  const sanitized: T = { ...event };

  if (sanitized.request) {
    sanitized.request = sanitizeRequest(sanitized.request);
  }

  if (sanitized.breadcrumbs) {
    sanitized.breadcrumbs = sanitized.breadcrumbs.map(scrubSentryBreadcrumb);
  }

  return sanitized;
};
